import { supabase } from '@/lib/supabase';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';

// Helper function to mask sensitive IDs in logs
const maskId = (id: string | undefined | null, visibleChars: number = 8): string => {
  if (!id || id.length <= visibleChars) return id || 'N/A';
  return `${id.substring(0, visibleChars)}...`;
};

export interface HealthReportData {
  car: {
    id: string;
    make: string;
    model: string;
    year: number;
    vin?: string;
    licensePlate?: string;
    nickname?: string;
    fuelType?: string;
    transmission?: string;
    imagePath?: string;
  };
  owner: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    phoneNumber?: string;
    avatarUrl?: string;
  };
  currentMileage: number | null;
  lastMileageDate?: string; // recorded_at from latest odometer reading
  serviceHistory: Array<{
    id: string;
    serviceDate: string;
    shopName?: string;
    mechanicName?: string;
    workDone: Array<{ title: string; notes?: string }>;
    findings?: string;
    recommendations?: string;
  }>;
  activeIssues: Array<{
    id: string;
    title?: string;
    description: string;
    aiSummary?: string;
    status: string;
    created_at: string;
    urgency?: number;
  }>;
  pendingTasks: Array<{
    id: string;
    title: string;
    description?: string;
    dueDate?: string;
    dueOdometer?: number;
    createdAt?: string;
    source: 'service_entry' | 'standalone';
  }>;
  fluidHistory: Array<{
    fluid_type: string;
    latest_spec_code?: string;
    latest_color?: string;
    last_refill_date?: string;
    last_replacement_date?: string;
    last_refill_odometer?: number;
    last_replacement_odometer?: number;
  }>;
  generatedAt: string;
}

/**
 * Generates a comprehensive health report for a car
 * @param carId - The ID of the car to generate the report for
 * @returns Health report data structure
 */
export async function generateHealthReport(carId: string): Promise<HealthReportData> {
  try {
    // 1. Fetch car basic info
    const { data: carData, error: carError } = await supabase
      .from('cars')
      .select('id, make, model, year, vin, license_plate, nickname, fuel_type, transmission, created_by, image_path')
      .eq('id', carId)
      .single();

    if (carError || !carData) {
      throw new Error(`Failed to fetch car data: ${carError?.message || 'Car not found'}`);
    }

    // 2. Fetch latest odometer reading
    const { data: odometerDataList } = await supabase
      .from('odometer_readings')
      .select('reading, recorded_at')
      .eq('car_id', carId)
      // Latest = highest reading value (regardless of date); if equal, newest date wins
      // This ensures we show the highest reading even if a newer entry has a lower value
      .order('reading', { ascending: false })
      .order('recorded_at', { ascending: false })
      .limit(10); // Fetch top 10 to check for outliers

    // Filter out obvious outliers
    const { filterOdometerOutliers } = await import('@/lib/utils');
    const validReading = odometerDataList && odometerDataList.length > 0
      ? filterOdometerOutliers(odometerDataList)
      : null;
    
    const currentMileage = validReading;
    const lastMileageDate = odometerDataList && odometerDataList.length > 0
      ? odometerDataList.find(r => r.reading === validReading)?.recorded_at
      : undefined;

    // 3. Fetch owner contact information
    let ownerInfo = {
      firstName: undefined as string | undefined,
      lastName: undefined as string | undefined,
      displayName: undefined as string | undefined,
      phoneNumber: undefined as string | undefined,
    };

    if (carData.created_by) {
      // Fetch from public.users table
      const { data: userData } = await supabase
        .from('users')
        .select('id, first_name, last_name, phone_number, custom_avatar_url')
        .eq('id', carData.created_by)
        .single();

      if (userData) {
        ownerInfo.firstName = userData.first_name || undefined;
        ownerInfo.lastName = userData.last_name || undefined;
        ownerInfo.phoneNumber = userData.phone_number || undefined;

        // Try to get display_name and avatar from auth metadata via Edge Function
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
          const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
            const response = await fetch(
              `${supabaseUrl}/functions/v1/get-user-display-names`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session.access_token}`,
                  'apikey': supabaseAnonKey,
                },
                body: JSON.stringify({ userIds: [carData.created_by] }),
              }
            );

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.displayNames && data.displayNames[carData.created_by]) {
                ownerInfo.displayName = data.displayNames[carData.created_by];
              }
            }
          }

          // Load avatar URL (custom_avatar_url takes precedence, then Google avatar from auth)
          if (userData.custom_avatar_url) {
            try {
              const signedUrlInfo = await getSignedViewUrl(userData.custom_avatar_url);
              ownerInfo.avatarUrl = signedUrlInfo.url;
            } catch (error) {
              console.error('Error fetching custom avatar:', error);
              // Fall through to Google avatar
            }
          }

          // If no custom avatar, try to get Google avatar from auth metadata (only for current user)
          if (!ownerInfo.avatarUrl) {
            const { data: { user: authUser } } = await supabase.auth.getUser();
            if (authUser && authUser.id === carData.created_by) {
              const meta: any = authUser.user_metadata || {};
              ownerInfo.avatarUrl = meta.avatar_url || meta.picture || undefined;
            }
          }
        } catch (error) {
          console.error('Error fetching display name or avatar:', error);
        }
      }
    }

    // 4. Fetch service history (all entries) - date, work done, findings, recommendations
    const { data: serviceEntries, error: serviceError } = await supabase
      .from('service_entries')
      .select('id, service_date, work_done, findings, recommendations, shop_name, mechanic_name')
      .eq('car_id', carId)
      .order('service_date', { ascending: false });

    if (serviceError) {
      console.error('Error fetching service entries:', serviceError);
    }

    const serviceHistory = (serviceEntries || []).map((entry: any) => ({
      id: entry.id,
      serviceDate: entry.service_date,
      shopName: entry.shop_name || undefined,
      mechanicName: entry.mechanic_name || undefined,
      workDone: Array.isArray(entry.work_done) ? entry.work_done : [],
      findings: entry.findings || undefined,
      recommendations: entry.recommendations || undefined,
    }));

    // 5. Fetch active issues (symptoms where status is 'open', 'recurred', or 'draft')
    // Query all non-deleted symptoms first, then filter by status
    const { data: allSymptoms, error: allSymptomsError } = await supabase
      .from('symptoms')
      .select('id, title, description, problems_stated, status, created_at, urgency')
      .eq('car_id', carId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (allSymptomsError) {
      console.error('Error fetching all symptoms:', allSymptomsError);
    }

    // Filter for active statuses (case-insensitive)
    const symptomsData = (allSymptoms || []).filter((symptom: any) => {
      const status = symptom.status?.toLowerCase()?.trim();
      return status === 'open' || status === 'recurred' || status === 'draft';
    });

    console.log('🔍 Debug: Active issues query:', {
      carId: maskId(carId),
      allSymptomsCount: allSymptoms?.length || 0,
      activeIssuesCount: symptomsData.length,
      allStatuses: allSymptoms?.map((s: any) => ({ id: maskId(s.id), status: s.status, title: s.title })),
      activeStatuses: symptomsData.map((s: any) => ({ id: maskId(s.id), status: s.status, title: s.title }))
    });

    const activeIssues = (symptomsData || []).map((symptom: any) => ({
      id: symptom.id,
      title: symptom.title,
      description: symptom.description,
      aiSummary: symptom.problems_stated,
      status: symptom.status,
      created_at: symptom.created_at,
      urgency: symptom.urgency,
    }));

    // 6. Fetch pending/open tasks only (same rules as Records / service entry checklist)
    const { data: { user: reportUser } } = await supabase.auth.getUser();
    const {
      addTaskOverlay,
      collectOpenSuggestedTasks,
    } = await import('@/lib/openTasks');

    const { data: serviceEntriesWithTasks } = await supabase
      .from('service_entries')
      .select('id, service_date, suggested_next_fixes, service_tasks_resolved')
      .eq('car_id', carId)
      .not('suggested_next_fixes', 'is', null);

    const { data: hiddenServiceTasksData } = reportUser?.id
      ? await supabase
          .from('hidden_service_tasks')
          .select('service_entry_id, task_text, hidden, status')
          .eq('user_id', reportUser.id)
          .eq('car_id', carId)
      : { data: [] as Array<{ service_entry_id: string; task_text: string; hidden: boolean; status?: string }> };

    const taskOverlayMap = new Map<string, { hidden: boolean; status?: string | null }>();
    (hiddenServiceTasksData || []).forEach((row: any) => {
      addTaskOverlay(taskOverlayMap, row.service_entry_id, row.task_text, {
        hidden: !!row.hidden,
        status: row.status,
      });
    });

    const serviceTasks = collectOpenSuggestedTasks({
      entries: serviceEntriesWithTasks || [],
      overlays: taskOverlayMap,
    });

    // Standalone: pending + scheduled only
    const { data: standaloneTasks, error: tasksError } = await supabase
      .from('tasks')
      .select('id, title, due_date, due_odometer, notes, created_at')
      .eq('car_id', carId)
      .in('status', ['pending', 'scheduled']);

    if (tasksError) {
      console.error('Error fetching tasks:', tasksError);
    }

    const standaloneTasksList = (standaloneTasks || []).map((task: any) => ({
      id: String(task.id),
      title: task.title,
      description: task.notes,
      dueDate: task.due_date,
      dueOdometer: task.due_odometer,
      createdAt: task.created_at,
      source: 'standalone' as const,
    }));

    const pendingTasks = [...serviceTasks, ...standaloneTasksList];

    // 7. Fetch fluid change history
    const { data: fluidsData, error: fluidsError } = await supabase
      .from('fluids')
      .select('*')
      .eq('car_id', carId)
      .order('action_date', { ascending: false });

    if (fluidsError) {
      console.error('Error fetching fluids:', fluidsError);
    }

    // Group fluids by type and get latest info
    const fluidTypes = ['engine_oil', 'brake_fluid', 'coolant', 'washer_fluid'];
    const fluidHistory: Array<{
      fluid_type: string;
      latest_spec_code?: string;
      latest_color?: string;
      last_refill_date?: string;
      last_replacement_date?: string;
      last_refill_odometer?: number;
      last_replacement_odometer?: number;
    }> = [];

    for (const fluidType of fluidTypes) {
      const typeFluids = (fluidsData || []).filter((f: any) => f.fluid_type === fluidType);
      
      if (typeFluids.length > 0) {
        const latest = typeFluids[0];
        const lastRefill = typeFluids.find((f: any) => f.action_type === 'refill');
        const lastReplacement = typeFluids.find((f: any) => f.action_type === 'replacement');
        
        // Fetch odometer readings for refill/replacement
        let refillOdometer: number | undefined;
        let replacementOdometer: number | undefined;
        
        try {
          if (lastRefill?.id) {
            const { data: odoRefill } = await supabase
              .from('odometer_readings')
              .select('reading')
              .eq('car_id', carId)
              .eq('related_id', lastRefill.id)
              .limit(1)
              .maybeSingle();
            refillOdometer = odoRefill?.reading ?? undefined;
          }
          if (lastReplacement?.id) {
            const { data: odoReplace } = await supabase
              .from('odometer_readings')
              .select('reading')
              .eq('car_id', carId)
              .eq('related_id', lastReplacement.id)
              .limit(1)
              .maybeSingle();
            replacementOdometer = odoReplace?.reading ?? undefined;
          }
        } catch (e) {
          // Ignore errors
        }

        fluidHistory.push({
          fluid_type: fluidType,
          latest_spec_code: latest.spec_code,
          latest_color: latest.color,
          last_refill_date: lastRefill?.action_date,
          last_replacement_date: lastReplacement?.action_date,
          last_refill_odometer: refillOdometer,
          last_replacement_odometer: replacementOdometer,
        });
      }
    }

    return {
      car: {
        id: carData.id,
        make: carData.make || '',
        model: carData.model || '',
        year: carData.year || 0,
        vin: carData.vin,
        licensePlate: carData.license_plate,
        nickname: carData.nickname,
        fuelType: carData.fuel_type,
        transmission: carData.transmission,
        imagePath: carData.image_path,
      },
      owner: ownerInfo,
      currentMileage,
      lastMileageDate,
      serviceHistory,
      activeIssues,
      pendingTasks,
      fluidHistory,
      generatedAt: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error generating health report:', error);
    throw error;
  }
}

